import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { redirectToUrl } from 'src/js/utils/redirect-to-url';
import { APP_NAME, PRODUCT_CATEGORY, premiumFeatures } from 'src/js/constants/common';
import configs from 'src/js/modules/config';
import sites from 'src/js/modules/sites';
import i18n from 'src/js/modules/i18n';
import { t } from 'i18next';
import subscriptions from 'src/js/modules/subscriptions';
import dialogs from '../index';
import SiteLimitModal from '../../../components/modals/site-limit';
import trackAsync from '../../analytics/segment/helpers/track-async';
import eventTypes from '../../analytics/segment/constants/event-types';
import purchaseFlowTypes from '../constants/purchase-flow-types';
import track from '../../analytics/segment/helpers/track';
import utils from '../../../utils';

const { subscriptionTypes } = subscriptions;

const { useMatchBreakpoint } = utils.hooks;

const getCaptions = ({ sitesCount, limit }) => ({
  title: t('Upgrade to add more websites'),
  titlePremium: t("You've reached your website limit."),
  limit: t('{sitesCount}/{limit} websites', { sitesCount, limit }),
  description: t('To continue using the selected website template, please upgrade your plan'),
  descriptionPremium: t(
    'To continue using the selected website template you will need to delete one of your existing websites.'
  ),
  viewWebsitesButton: t('View your websites'),
  upgradeButton: t('Upgrade Now'),
});

const DESKTOP_VIEW_BREAKPOINT = '576px';

function SiteLimitDialogContainer({ templateSlug, backUrl = '' }) {
  const [isLoading, setIsLoading] = useState(false);
  const currentPackage = useSelector(subscriptions.selectors.getCurrentPackage);
  const limit = useSelector(subscriptions.selectors.getCurrentPackageSiteLimit);
  const myyolaLink = useSelector(configs.selectors.getMyyolaUrl);
  const yolacomLink = useSelector(configs.selectors.getYolacomUrl);
  const upgradeUrl = useSelector(configs.selectors.getUpgradeUrl);
  const locale = useSelector(i18n.selectors.getLocale);

  const isPremium =
    currentPackage.type === subscriptionTypes.GOLD ||
    currentPackage.type === subscriptionTypes.SILVER;
  const sitesCount = useSelector(sites.selectors.getSitesCount);

  const captions = useMemo(() => getCaptions({ sitesCount, limit }), [sitesCount, limit]);
  const isDesktopView = useMatchBreakpoint(DESKTOP_VIEW_BREAKPOINT);

  const imageSrc = useMemo(
    () =>
      isDesktopView
        ? '/images/upgrade-interruption-site-limit/desktop-illustration.jpg'
        : '/images/upgrade-interruption-site-limit/mobile-illustration.jpg',
    [isDesktopView]
  );

  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    dispatch(dialogs.actions.hide());
  }, [dispatch]);

  useEffect(() => {
    track(eventTypes.UPGRADE_INTERRUPTION_DISPLAYED, {
      appName: APP_NAME,
      templateBuildSlug: templateSlug,
      premiumFeature: premiumFeatures.SITES_COUNT,
      productCategory: PRODUCT_CATEGORY,
      purchaseFlowType: purchaseFlowTypes.B2C,
      siteId: null,
    });
  }, [templateSlug]);

  const onUpgrade = useCallback(async () => {
    setIsLoading(true);

    const upgradeURLWithParams = new URL(upgradeUrl);

    if (backUrl) {
      upgradeURLWithParams.searchParams.append('back', backUrl.replace('/create', ''));
    }

    const upgradelink = upgradeURLWithParams.toString();

    await Promise.all([
      trackAsync(eventTypes.UPGRADE_INTERRUPTION_SUBMITTED, {
        appName: APP_NAME,
        siteId: null,
        templateBuildSlug: templateSlug,
        purchaseFlowType: purchaseFlowTypes.B2C,
        productCategory: PRODUCT_CATEGORY,
        premiumFeature: premiumFeatures.SITES_COUNT,
      }),
      trackAsync(eventTypes.UPGRADE_INITIATED, {
        appName: APP_NAME,
        templateBuildSlug: templateSlug,
        premiumFeature: premiumFeatures.SITES_COUNT,
        productCategory: PRODUCT_CATEGORY,
        purchaseFlowType: purchaseFlowTypes.B2C,
        siteId: null,
      }),
    ]);

    redirectToUrl(upgradelink);
    setIsLoading(false);
  }, [yolacomLink, locale, templateSlug]);

  const onRedirect = () => {
    redirectToUrl(myyolaLink);
  };

  return (
    <SiteLimitModal
      captions={captions}
      imageSrc={imageSrc}
      isLoading={isLoading}
      isDesktopView={isDesktopView}
      isPremium={isPremium}
      onClose={onClose}
      onUpgrade={onUpgrade}
      onRedirectToDashboard={onRedirect}
    />
  );
}

SiteLimitDialogContainer.propTypes = {
  templateSlug: PropTypes.string.isRequired,
  backUrl: PropTypes.string,
};

export default SiteLimitDialogContainer;
